import { createContext, useCallback, useContext } from 'react';

/**
 * Widget chrome localization: the widget's own UI strings, never the agent's
 * configured texts. Languages mirror `apps/backend/src/shared/text/languages.py`.
 */

export type WidgetLanguage = 'es' | 'en' | 'fr' | 'de';

export const BASE_LANGUAGE: WidgetLanguage = 'es';

export const SUPPORTED_LANGUAGES: readonly WidgetLanguage[] = ['es', 'en', 'fr', 'de'];

/** Normalize a language tag (`fr-FR`, `EN_us`) to a supported language, or `null`. */
export function normalizeLanguage(raw: string | null | undefined): WidgetLanguage | null {
  if (!raw) return null;
  const base = raw.trim().toLowerCase().replace('_', '-').split('-', 1)[0];
  return SUPPORTED_LANGUAGES.find((lang) => lang === base) ?? null;
}

const ES = {
  'header.back': 'Atrás',
  'header.minimize': 'Minimizar',
  'header.newChat': 'Nueva conversación',
  'header.close': 'Cerrar',

  'position.menu': 'Menú de posición',
  'position.leftHalf': 'Mitad izquierda',
  'position.rightHalf': 'Mitad derecha',
  'position.defaultSize': 'Tamaño por defecto',

  'input.placeholder': '¿En qué podemos ayudarte?',
  'input.waitAMoment': 'Espera un momento...',
  'input.messageInput': 'Campo de mensaje',
  'input.sending': 'Enviando mensaje...',
  'input.send': 'Enviar mensaje',

  'confirm.newChatTitle': '¿Quieres empezar una nueva conversación?',
  'confirm.newChatDescription': 'Se borrarán los mensajes anteriores',
  'confirm.cancel': 'Cancelar',
  'confirm.newChat': 'Nueva conversación',

  'faq.greeting': 'Hola',
  'faq.howCanWeHelp': '¿En qué podemos ayudarte?',
  'faq.trending': 'Tendencias',
  'faq.askWhatYouWant': 'Pregúntame lo que quieras',
  'faq.selectTemplate': 'Seleccionar: {label}',
  'faq.legalPrefix': 'Al continuar, confirmas que aceptas nuestra',
  'faq.legalPrivacy': 'política de privacidad',
  'faq.legalAnd': 'y los',
  'faq.legalTerms': 'términos y condiciones',

  'thinking.default': 'Pensando',
  'thinking.executingTool': 'Ejecutando herramienta {tool}',
  'thinking.displaying': 'Mostrando',
  'thinking.analyzing': 'Analizando tu consulta',

  'searchbar.placeholder': 'Pregunta cualquier cosa…',
  'searchbar.voiceSearch': 'Búsqueda por voz',
  'searchbar.aiModeSearch': 'Búsqueda con IA',
  'searchbar.startHere': 'Empieza por aquí',
  'searchbar.newConversation': 'Nueva conversación',
  'searchbar.quickActionsHeading': '¿Seguimos afinando?',

  'form.submitted': 'Formulario enviado',
  'form.submit': 'Enviar',
  'form.submitting': 'Enviando...',
  'form.close': 'Cerrar',
  'form.selectOption': 'Selecciona una opción',
  'form.required': 'Este campo es obligatorio',
  'form.invalidEmail': 'Introduce un correo electrónico válido',
  'form.invalidPhone': 'Introduce un número de teléfono válido',
  'form.success': '¡Formulario enviado correctamente!',
  'form.error': 'No se ha podido enviar el formulario. Inténtalo de nuevo.',
  'form.phoneNumber': 'Número de teléfono',
  'form.searchCountries': 'Buscar países...',
  'form.noCountries': 'No se han encontrado países',
  'form.selectCountry': 'Selecciona un país',

  'carousel.scrollLeft': 'Desplazar a la izquierda',
  'carousel.scrollRight': 'Desplazar a la derecha',
  'carousel.seeMore': 'Ver más',

  'contact.description':
    'Si no encuentras lo que estás buscando, nuestros asesores comerciales pueden brindarte un servicio más personalizado.',

  'chat.errorSend': 'No se ha podido enviar el mensaje',
  'chat.stillAnswering': 'Espera la respuesta antes de enviar otro mensaje',
  'chat.errorSubmitForm': 'No se ha podido enviar el formulario',
  'chat.errorLoadForm': 'No se ha podido cargar el formulario',
  'chat.errorReply': 'No he podido responder. Vuelve a intentarlo en un momento.',

  'common.logoAlt': 'Logo',
  'common.chatAlt': 'Chat',
  'common.resizeHandle': 'Redimensionar {direction}',
} as const;

export type WidgetStringKey = keyof typeof ES;

export const WIDGET_STRING_KEYS = Object.keys(ES) as WidgetStringKey[];

const EN: Record<WidgetStringKey, string> = {
  'header.back': 'Back',
  'header.minimize': 'Minimize',
  'header.newChat': 'New chat',
  'header.close': 'Close',

  'position.menu': 'Position menu',
  'position.leftHalf': 'Left half',
  'position.rightHalf': 'Right half',
  'position.defaultSize': 'Default size',

  'input.placeholder': 'How can we help you?',
  'input.waitAMoment': 'Wait a moment...',
  'input.messageInput': 'Message input',
  'input.sending': 'Sending message...',
  'input.send': 'Send message',

  'confirm.newChatTitle': 'Do you want to start a new conversation?',
  'confirm.newChatDescription': 'Previous messages will be deleted',
  'confirm.cancel': 'Cancel',
  'confirm.newChat': 'New conversation',

  'faq.greeting': 'Hello',
  'faq.howCanWeHelp': 'How can we help you?',
  'faq.trending': 'Trending',
  'faq.askWhatYouWant': 'Ask me anything',
  'faq.selectTemplate': 'Select: {label}',
  'faq.legalPrefix': 'By continuing, you confirm that you accept our',
  'faq.legalPrivacy': 'privacy policy',
  'faq.legalAnd': 'and the',
  'faq.legalTerms': 'terms and conditions',

  'thinking.default': 'Thinking',
  'thinking.executingTool': 'Executing tool {tool}',
  'thinking.displaying': 'Displaying',
  'thinking.analyzing': 'Analyzing your question',

  'searchbar.placeholder': 'Ask anything…',
  'searchbar.voiceSearch': 'Voice search',
  'searchbar.aiModeSearch': 'AI mode search',
  'searchbar.startHere': 'Start here',
  'searchbar.newConversation': 'New conversation',
  'searchbar.quickActionsHeading': 'Shall we keep refining?',

  'form.submitted': 'Form submitted',
  'form.submit': 'Submit',
  'form.submitting': 'Submitting...',
  'form.close': 'Close',
  'form.selectOption': 'Select an option',
  'form.required': 'This field is required',
  'form.invalidEmail': 'Please enter a valid email address',
  'form.invalidPhone': 'Please enter a valid phone number',
  'form.success': 'Form submitted successfully!',
  'form.error': 'Failed to submit form. Please try again.',
  'form.phoneNumber': 'Phone number',
  'form.searchCountries': 'Search countries...',
  'form.noCountries': 'No countries found',
  'form.selectCountry': 'Select a country',

  'carousel.scrollLeft': 'Scroll left',
  'carousel.scrollRight': 'Scroll right',
  'carousel.seeMore': 'See more',

  'contact.description':
    "If you can't find what you are looking for, our sales advisors can offer you a more personalized service.",

  'chat.errorSend': 'Failed to send message',
  'chat.stillAnswering': 'Wait for the reply before sending another message',
  'chat.errorSubmitForm': 'Failed to submit form',
  'chat.errorLoadForm': 'Failed to load form',
  'chat.errorReply': 'I couldn’t answer that. Please try again in a moment.',

  'common.logoAlt': 'Logo',
  'common.chatAlt': 'Chat',
  'common.resizeHandle': 'Resize {direction}',
};

const FR: Record<WidgetStringKey, string> = {
  'header.back': 'Retour',
  'header.minimize': 'Réduire',
  'header.newChat': 'Nouvelle discussion',
  'header.close': 'Fermer',

  'position.menu': 'Menu de position',
  'position.leftHalf': 'Moitié gauche',
  'position.rightHalf': 'Moitié droite',
  'position.defaultSize': 'Taille par défaut',

  'input.placeholder': 'Comment pouvons-nous vous aider ?',
  'input.waitAMoment': 'Un instant...',
  'input.messageInput': 'Champ de message',
  'input.sending': 'Envoi du message...',
  'input.send': 'Envoyer le message',

  'confirm.newChatTitle': 'Voulez-vous démarrer une nouvelle conversation ?',
  'confirm.newChatDescription': 'Les messages précédents seront supprimés',
  'confirm.cancel': 'Annuler',
  'confirm.newChat': 'Nouvelle conversation',

  'faq.greeting': 'Bonjour',
  'faq.howCanWeHelp': 'Comment pouvons-nous vous aider ?',
  'faq.trending': 'Tendances',
  'faq.askWhatYouWant': 'Posez-moi toutes vos questions',
  'faq.selectTemplate': 'Sélectionner : {label}',
  'faq.legalPrefix': 'En continuant, vous confirmez accepter notre',
  'faq.legalPrivacy': 'politique de confidentialité',
  'faq.legalAnd': 'et les',
  'faq.legalTerms': 'conditions générales',

  'thinking.default': 'Réflexion',
  'thinking.executingTool': 'Exécution de l’outil {tool}',
  'thinking.displaying': 'Affichage',
  'thinking.analyzing': 'Analyse de votre demande',

  'searchbar.placeholder': 'Posez n’importe quelle question…',
  'searchbar.voiceSearch': 'Recherche vocale',
  'searchbar.aiModeSearch': 'Recherche avec IA',
  'searchbar.startHere': 'Commencez ici',
  'searchbar.newConversation': 'Nouvelle conversation',
  'searchbar.quickActionsHeading': 'On continue à affiner ?',

  'form.submitted': 'Formulaire envoyé',
  'form.submit': 'Envoyer',
  'form.submitting': 'Envoi en cours...',
  'form.close': 'Fermer',
  'form.selectOption': 'Sélectionnez une option',
  'form.required': 'Ce champ est obligatoire',
  'form.invalidEmail': 'Veuillez saisir une adresse e-mail valide',
  'form.invalidPhone': 'Veuillez saisir un numéro de téléphone valide',
  'form.success': 'Formulaire envoyé avec succès !',
  'form.error': 'Échec de l’envoi du formulaire. Veuillez réessayer.',
  'form.phoneNumber': 'Numéro de téléphone',
  'form.searchCountries': 'Rechercher un pays...',
  'form.noCountries': 'Aucun pays trouvé',
  'form.selectCountry': 'Sélectionnez un pays',

  'carousel.scrollLeft': 'Défiler vers la gauche',
  'carousel.scrollRight': 'Défiler vers la droite',
  'carousel.seeMore': 'Voir plus',

  'contact.description':
    'Si vous ne trouvez pas ce que vous cherchez, nos conseillers commerciaux peuvent vous offrir un service plus personnalisé.',

  'chat.errorSend': 'Échec de l’envoi du message',
  'chat.stillAnswering': 'Attendez la réponse avant d’envoyer un autre message',
  'chat.errorSubmitForm': 'Échec de l’envoi du formulaire',
  'chat.errorLoadForm': 'Échec du chargement du formulaire',
  'chat.errorReply': 'Je n’ai pas pu répondre. Veuillez réessayer dans un instant.',

  'common.logoAlt': 'Logo',
  'common.chatAlt': 'Chat',
  'common.resizeHandle': 'Redimensionner {direction}',
};

const DE: Record<WidgetStringKey, string> = {
  'header.back': 'Zurück',
  'header.minimize': 'Minimieren',
  'header.newChat': 'Neuer Chat',
  'header.close': 'Schließen',

  'position.menu': 'Positionsmenü',
  'position.leftHalf': 'Linke Hälfte',
  'position.rightHalf': 'Rechte Hälfte',
  'position.defaultSize': 'Standardgröße',

  'input.placeholder': 'Wie können wir Ihnen helfen?',
  'input.waitAMoment': 'Einen Moment...',
  'input.messageInput': 'Nachrichtenfeld',
  'input.sending': 'Nachricht wird gesendet...',
  'input.send': 'Nachricht senden',

  'confirm.newChatTitle': 'Möchten Sie eine neue Unterhaltung beginnen?',
  'confirm.newChatDescription': 'Vorherige Nachrichten werden gelöscht',
  'confirm.cancel': 'Abbrechen',
  'confirm.newChat': 'Neue Unterhaltung',

  'faq.greeting': 'Hallo',
  'faq.howCanWeHelp': 'Wie können wir Ihnen helfen?',
  'faq.trending': 'Beliebt',
  'faq.askWhatYouWant': 'Fragen Sie mich alles',
  'faq.selectTemplate': 'Auswählen: {label}',
  'faq.legalPrefix': 'Wenn Sie fortfahren, akzeptieren Sie unsere',
  'faq.legalPrivacy': 'Datenschutzerklärung',
  'faq.legalAnd': 'und die',
  'faq.legalTerms': 'allgemeinen Geschäftsbedingungen',

  'thinking.default': 'Denkt nach',
  'thinking.executingTool': 'Tool {tool} wird ausgeführt',
  'thinking.displaying': 'Wird angezeigt',
  'thinking.analyzing': 'Ihre Anfrage wird analysiert',

  'searchbar.placeholder': 'Fragen Sie alles…',
  'searchbar.voiceSearch': 'Sprachsuche',
  'searchbar.aiModeSearch': 'Suche mit KI',
  'searchbar.startHere': 'Hier starten',
  'searchbar.newConversation': 'Neue Unterhaltung',
  'searchbar.quickActionsHeading': 'Sollen wir weiter eingrenzen?',

  'form.submitted': 'Formular gesendet',
  'form.submit': 'Senden',
  'form.submitting': 'Wird gesendet...',
  'form.close': 'Schließen',
  'form.selectOption': 'Option auswählen',
  'form.required': 'Dieses Feld ist erforderlich',
  'form.invalidEmail': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
  'form.invalidPhone': 'Bitte geben Sie eine gültige Telefonnummer ein',
  'form.success': 'Formular erfolgreich gesendet!',
  'form.error': 'Formular konnte nicht gesendet werden. Bitte erneut versuchen.',
  'form.phoneNumber': 'Telefonnummer',
  'form.searchCountries': 'Länder suchen...',
  'form.noCountries': 'Keine Länder gefunden',
  'form.selectCountry': 'Land auswählen',

  'carousel.scrollLeft': 'Nach links scrollen',
  'carousel.scrollRight': 'Nach rechts scrollen',
  'carousel.seeMore': 'Mehr anzeigen',

  'contact.description':
    'Wenn Sie nicht finden, wonach Sie suchen, bieten Ihnen unsere Vertriebsberater einen persönlicheren Service.',

  'chat.errorSend': 'Nachricht konnte nicht gesendet werden',
  'chat.stillAnswering': 'Warte auf die Antwort, bevor du eine weitere Nachricht sendest',
  'chat.errorSubmitForm': 'Formular konnte nicht gesendet werden',
  'chat.errorLoadForm': 'Formular konnte nicht geladen werden',
  'chat.errorReply': 'Ich konnte nicht antworten. Bitte versuche es gleich noch einmal.',

  'common.logoAlt': 'Logo',
  'common.chatAlt': 'Chat',
  'common.resizeHandle': 'Größe ändern: {direction}',
};

const DICTIONARIES: Record<WidgetLanguage, Record<WidgetStringKey, string>> = {
  es: ES,
  en: EN,
  fr: FR,
  de: DE,
};

/** Resolve a chrome string, falling back to the base language, then the key. */
export function t(
  key: WidgetStringKey,
  lang: WidgetLanguage,
  params?: Record<string, string>,
): string {
  const template = DICTIONARIES[lang]?.[key] ?? DICTIONARIES[BASE_LANGUAGE][key] ?? key;
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => params[name] ?? match);
}

/** The visitor language the widget renders its chrome in; base language without a provider. */
export const WidgetLangContext = createContext<WidgetLanguage>(BASE_LANGUAGE);

/** The active widget language, for data outside the dictionary (country names). */
export function useWidgetLanguage(): WidgetLanguage {
  return useContext(WidgetLangContext);
}

/** Translate helper bound to the active widget language. */
export function useT(): (key: WidgetStringKey, params?: Record<string, string>) => string {
  const lang = useContext(WidgetLangContext);
  return useCallback(
    (key: WidgetStringKey, params?: Record<string, string>) => t(key, lang, params),
    [lang],
  );
}
